import { action, makeObservable, observable, reaction } from 'mobx';

import { AxiosError } from 'axios';

import _ from 'lodash';

import API from 'utils/api';
import DataMixin from 'utils/stores/DataMixin';

import { EntityModules } from 'core/constants';

import { RelevantFieldType } from 'utils/api/modules/entities/entities.types';

import Logger from 'utils/logger';

import RootStore from '../Root';

export type PipelineStageType = {
  uuid?: string;
  name: string;
  key: string;
  guidance: string;
  closed?: boolean;
  active?: boolean;
  relevantFields?: RelevantFieldType[];
  stageIsUsedInEntities?: boolean;
};

export type PipelineDetailsType = {
  uuid: string;
  name: string;
  key: string;
  entityType: EntityModules;
  createdAt: string;
  updatedAt: string;
  order: string[];
  stages: PipelineStageType[];
  default: boolean;
};

export default class PipelineDetails extends DataMixin {
  @observable data?: PipelineDetailsType;

  @observable error?: AxiosError;

  @observable selectedStage?: string;

  @observable isModalOpenForCreatingStage = false;

  @observable initialData?: PipelineDetailsType;

  @observable contentWasModified = false;

  constructor(rootStore: RootStore, api: typeof API) {
    super(rootStore, api);
    makeObservable(this);

    reaction(
      () => JSON.stringify(this.data),
      (data) => {
        if (!this.initialData || !data) {
          return;
        }

        if (_.isEqual(JSON.parse(data), JSON.parse(JSON.stringify(this.initialData)))) {
          this.contentWasModified = false;
        } else {
          this.contentWasModified = true;
        }
      }
    );
  }

  @action loadDetails = async (pipelineId: string): Promise<void> => {
    this.isLoaded = false;
    try {
      const { data } = await this.api.entities.loadPipelineDetails(EntityModules.Events, pipelineId)();
      this.data = data;
      this.initialData = _.cloneDeep(data);
      if (this.data.order.length > 0) {
        const [firstStage] = this.data.order;
        this.selectedStage = firstStage;
      }
    } catch (error) {
      this.error = error as AxiosError;
    } finally {
      this.isLoaded = true;
    }
    this.contentWasModified = false;
  };

  @action setSelectedStage = (stageKey: string): void => {
    this.selectedStage = stageKey;
  };

  @action updateOrder = (order: string[]): void => {
    if (!this.data) {
      return;
    }

    this.data.order = order;
  };

  @action updateStageData = (stageKey: string, data: Partial<PipelineStageType>): void => {
    if (!this.data) {
      return;
    }

    const stage = this.data.stages.find(({ key }) => key === stageKey);

    if (!stage) {
      return;
    }

    this.data.stages = this.data.stages.map((s) => {
      if (s.key === stageKey) {
        return { ...stage, ...data };
      }
      return s;
    });
  };

  @action addStage = (stageData: { name: string; key: string }): boolean => {
    if (!this.data) {
      return false;
    }

    const newStage: PipelineStageType = {
      name: stageData.name,
      key: stageData.key,
      guidance: '',
      relevantFields: [],
    };

    // check if stage with the same name already exists
    if (this.data.stages.some((stage) => stage.name === newStage.name)) {
      this.store.notification.enqueueErrorSnackbar('Stage name must be unique. A stage with this name already exists.');
      return false;
    }

    // check if stage with the same key already exists
    if (this.data.stages.some((stage) => stage.key === newStage.key)) {
      this.store.notification.enqueueErrorSnackbar(
        'This stage name conflicts with an existing system reference and can’t be used. Please choose a different name, or reach out to Support if you need assistance.'
      );
      return false;
    }

    this.data.stages.push(newStage);

    // the closed stage should be always at the end of the order, so we need to add new stage before it
    const closedStageIndex = this.data.order.findIndex(
      (stage) => this.data?.stages.find((s) => s.key === stage)?.closed
    );
    if (closedStageIndex !== -1) {
      this.data.order.splice(closedStageIndex, 0, newStage.key);
    } else {
      this.data.order.push(newStage.key);
    }
    return true;
  };

  @action setIsModalOpenForCreatingStage = (isOpen: boolean): void => {
    this.isModalOpenForCreatingStage = isOpen;
  };

  @action publishPipeline = async (pipelineId: string): Promise<void> => {
    if (!this.data) {
      return;
    }

    // validate if all stages has name
    const hasNoNameStages = this.data.stages.some((stage) => !stage.name);
    if (hasNoNameStages) {
      this.store.notification.enqueueErrorSnackbar('All stages must have a name');
      return;
    }

    try {
      const { data } = await this.api.entities.updatePipeline(EntityModules.Events, pipelineId, {
        ...this.data,
      })();
      this.data = data;
      this.initialData = _.cloneDeep(data);
      this.contentWasModified = false;
      this.store.notification.enqueueSuccessSnackbar('Pipeline published successfully');
    } catch (error) {
      const err = error as AxiosError;
      Logger.error('Failed to publish pipeline', err);
      Object.values(err.response?.data as Record<string, string[]>).forEach((message) => {
        this.store.notification.enqueueErrorSnackbar(message.join(', ') || 'Failed to publish pipeline');
      });
    }
  };

  @action onDeleteStage = (): void => {
    if (!this.data) {
      return;
    }
    const stageName = this.data.stages.find((stage) => stage.key === this.selectedStage)?.name;

    // remove currently selected stage from order
    this.data.order = this.data.order.filter((stage) => stage !== this.selectedStage);

    // remove stage from stages
    // if field was not in initial data remove it from stages
    if (!this.initialData?.stages.some((stage) => stage.key === this.selectedStage)) {
      this.data.stages = this.data.stages.filter((stage) => stage.key !== this.selectedStage);
    }

    // set active stage to false
    this.data.stages.forEach((stage) => {
      if (stage.key === this.selectedStage) {
        stage.active = false;
      }
    });

    // set next stage as selected
    if (this.data.order.length > 0) {
      const [firstStage] = this.data.order;
      this.selectedStage = firstStage;
    } else {
      this.selectedStage = undefined;
    }

    this.store.notification.enqueueSuccessSnackbar(`The stage "${stageName}" has been deleted.`);
  };

  @action reset = (): void => {
    this.data = undefined;
    this.error = undefined;
    this.isLoaded = false;
    this.selectedStage = undefined;
    this.isModalOpenForCreatingStage = false;
  };
}
